import { z } from 'zod';

/**
 * Schemas Zod du module Reporting (Phase 12 — US-DASH-01 + US-REP-01 à 05).
 *
 *  - Dashboard global multi-modules (US-DASH-01)
 *  - Vue d'ensemble reporting avec comparaison période (US-REP-01)
 *  - Rapports équipements / infrastructure / interventions (US-REP-02/03/04)
 *  - Génération Excel (US-REP-05) + traçabilité via export_jobs
 */

// ─── Constantes ─────────────────────────────────────────────────────────────

export const EXPORT_FORMATS = ['xlsx', 'pdf', 'csv'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

export const EXPORT_TYPES = [
  'equipements',
  'interventions',
  'batiments',
  'services',
  'personnels',
  'audit_logs',
  'dashboard',
  'infrastructure',
] as const;
export type ExportType = (typeof EXPORT_TYPES)[number];

export const EXPORT_JOB_STATUTS = ['queued', 'running', 'completed', 'failed'] as const;
export type ExportJobStatut = (typeof EXPORT_JOB_STATUTS)[number];

// ─── Helpers de query communs ───────────────────────────────────────────────

/** Coerce une date ISO en Date. Accepte aussi YYYY-MM-DD. */
const isoDate = z
  .string()
  .min(1)
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Date invalide');

// ─── Dashboard (US-DASH-01) ────────────────────────────────────────────────

export const dashboardQuerySchema = z.object({
  /** ISO 8601 — défaut : 30 jours avant aujourd'hui. */
  date_from: isoDate.optional(),
  /** ISO 8601 — défaut : maintenant. */
  date_to: isoDate.optional(),
  service_id: z.string().uuid().optional(),
  batiment_id: z.string().uuid().optional(),
});
export type DashboardQuery = z.infer<typeof dashboardQuerySchema>;

// ─── Reporting filters (US-REP-02/03/04) ────────────────────────────────────

/**
 * Filtres communs à tous les rapports détaillés. Selon le type de rapport
 * certains champs sont ignorés (ex. `criticite` n'a pas de sens pour
 * `infrastructure`). La validation lâche permet de partager un schéma unique.
 */
export const reportingFiltersSchema = z.object({
  date_from: isoDate.optional(),
  date_to: isoDate.optional(),
  service_id: z.string().uuid().optional(),
  batiment_id: z.string().uuid().optional(),
  etage_id: z.string().uuid().optional(),
  salle_id: z.string().uuid().optional(),
  /** Selon le rapport : type d'équipement, de bâtiment, de demande, etc. */
  type: z.string().max(100).optional(),
  statut: z.string().max(100).optional(),
  /** Niveau de criticité d'équipement : vital | critique | important | standard. */
  criticite: z.string().max(50).optional(),
});
export type ReportingFilters = z.infer<typeof reportingFiltersSchema>;

// ─── Export jobs (US-REP-05) ────────────────────────────────────────────────

export const createExportJobSchema = z.object({
  type: z.enum(EXPORT_TYPES),
  format: z.enum(EXPORT_FORMATS),
  filters: z.record(z.string(), z.unknown()).optional(),
});
export type CreateExportJobInput = z.infer<typeof createExportJobSchema>;

export const listExportJobsQuerySchema = z.object({
  type: z.enum(EXPORT_TYPES).optional(),
  statut: z.enum(EXPORT_JOB_STATUTS).optional(),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListExportJobsQuery = z.infer<typeof listExportJobsQuerySchema>;
